if (!customElements.get('collection-mobile-filters')) {
  customElements.define('collection-mobile-filters', class extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onKey = this.onKey.bind(this);
      this.onToggle = this.onToggle.bind(this);
      this.sync = this.sync.bind(this);
    }

    connectedCallback() {
      this.mobile = window.matchMedia('(max-width: 899px)');
      this.section = this.closest('collection-section');
      this.addEventListener('click', this.onClick, true);
      this.addEventListener('keydown', this.onKey, true);
      this.section.addEventListener('collection:filter-drawer-toggle', this.onToggle);
      document.addEventListener('facets:change', this.sync);
      this.mobile.addEventListener('change', this.sync);
      this.sync();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick, true);
      this.removeEventListener('keydown', this.onKey, true);
      this.section.removeEventListener('collection:filter-drawer-toggle', this.onToggle);
      document.removeEventListener('facets:change', this.sync);
      this.mobile.removeEventListener('change', this.sync);
    }

    filters() { return [...this.querySelectorAll('collapsible-section.filter')]; }

    sync() {
      const colorBody = this.querySelector('.filter--color-option .filter__body');
      if (colorBody?.querySelector('collection-color-filter') && !colorBody.querySelector('collection-filter-color-families')) {
        colorBody.append(document.createElement('collection-filter-color-families'));
      }
      colorBody?.querySelector('collection-filter-color-families')?.refresh?.();
      const filters = this.filters();
      filters.filter(filter => !filter.classList.contains('filter--color-option')).forEach(filter => {
        const count = filter.querySelector('.facets__selected');
        const selectedCount = filter.querySelectorAll('input[name]:checked').length;
        if (count) count.textContent = selectedCount ? `(${selectedCount})` : '';
      });
      const selected = filters.find(filter => filter.dataset.mobileActive === 'true') || filters[0];
      if (this.mobile.matches) this.select(selected);
      else filters.forEach(filter => {
        filter.removeAttribute('data-mobile-active');
        const open = filter.dataset.open === 'true';
        filter.querySelector('.filter__header').setAttribute('aria-expanded', String(open));
        const focusable = open || this.querySelector('facet-filters-form').dataset.position === 'always';
        filter.querySelectorAll('.filter__body input, .filter__body button, .filter__body [tabindex]').forEach(input => {
          input.tabIndex = focusable ? 0 : -1;
        });
      });
    }

    select(selected) {
      this.filters().forEach(filter => {
        const active = filter === selected;
        filter.dataset.mobileActive = String(active);
        filter.querySelector('.filter__header').setAttribute('aria-expanded', String(active));
        filter.querySelectorAll('.filter__body input, .filter__body button, .filter__body [tabindex]').forEach(input => {
          input.tabIndex = active ? 0 : -1;
        });
      });
    }

    onToggle(event) {
      if (event.detail.open) this.sync();
      else queueMicrotask(this.sync);
    }

    onClick(event) {
      const header = event.target.closest('.filter__header');
      if (!this.mobile.matches || !header) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.select(header.closest('collapsible-section'));
    }

    onKey(event) {
      const header = event.target.closest('.filter__header');
      if (!this.mobile.matches || !header) return;
      if (!['Enter', ' ', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const filters = this.filters();
      let index = filters.indexOf(header.closest('collapsible-section'));
      if (event.key === 'ArrowUp') index = (index - 1 + filters.length) % filters.length;
      if (event.key === 'ArrowDown') index = (index + 1) % filters.length;
      if (event.key === 'Home') index = 0;
      if (event.key === 'End') index = filters.length - 1;
      this.select(filters[index]);
      filters[index].querySelector('.filter__header').focus();
    }
  });
}

if (!customElements.get('collection-filter-color-families')) {
  customElements.define('collection-filter-color-families', class extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onKey = this.onKey.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('keydown', this.onKey);
      this.resizeObserver = new ResizeObserver(() => this.layout());
      this.resizeObserver.observe(this);
      this.build();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('keydown', this.onKey);
      this.resizeObserver?.disconnect();
    }

    layout() {
      const columns = getComputedStyle(this).gridTemplateColumns.split(' ').length || 4;
      this.families?.forEach((family, index) => {
        family.panel.style.order = (Math.floor(index / columns) + 1) * columns * 2 + 1;
      });
    }

    build() {
      if (this.families) return;
      this.source = this.closest('[data-section-id]').querySelector('collection-color-families');
      if (!this.source?.families?.length || this.source.families.some(family => !Array.isArray(family.colors))) return;
      this.form = this.closest('form') || this.closest('[data-section-id]').querySelector('#FacetFiltersForm');
      const sourceFamilies = [...this.source.families.filter(family => family.isAll), ...this.source.families.filter(family => !family.isAll)]
        .filter(family => family.colors.length > 0);
      this.families = [];
      this.replaceChildren();
      sourceFamilies.forEach((family, index) => {
        const button = family.button.cloneNode(true);
        button.className = 'collection-filter-families__button';
        button.removeAttribute('data-family');
        button.removeAttribute('data-family-all');
        button.dataset.drawerFamily = index;
        button.style.order = (index + 1) * 2;
        button.setAttribute('aria-expanded', 'false');
        const panel = document.createElement('div');
        panel.className = 'collection-filter-families__panel';
        panel.id = `${family.panel.id}-drawer`;
        panel.hidden = true;
        button.setAttribute('aria-controls', panel.id);
        const all = family.panel.querySelector('.collection-color-families__select-all').cloneNode(true);
        all.className = 'collection-filter-families__all';
        all.querySelector('input').dataset.drawerSelectAll = index;
        panel.append(all);
        const grid = document.createElement('div');
        grid.className = 'collection-filter-families__colors';
        const colors = family.colors.map(color => {
          const label = color.checkbox.parentElement.cloneNode(true);
          label.className = 'collection-filter-families__color';
          const proxy = label.querySelector('input');
          proxy.removeAttribute('name');
          proxy.dataset.drawerColor = '';
          const inputs = [...new Set(color.values)].map(value => {
            let input = [...this.form.querySelectorAll('input[name]')].find(item => item.name === this.source.paramName && item.value === value && item.type === 'checkbox');
            if (!input) {
              this.form.querySelectorAll('[data-family-selections] input').forEach(item => {
                if (item.value === value) item.remove();
              });
              input = document.createElement('input');
              input.type = 'checkbox';
              input.name = this.source.paramName;
              input.value = value;
              input.checked = this.source.selected.has(value);
              input.hidden = true;
              input.dataset.drawerColorValue = '';
              this.form.append(input);
            }
            return input;
          });
          grid.append(label);
          return { proxy, inputs };
        });
        panel.append(grid);
        this.append(button, panel);
        this.families.push({ button, panel, all: all.querySelector('input'), colors, isAll: family.isAll });
      });
      this.layout();
      this.refresh();
    }

    refresh() {
      if (!this.families) { this.build(); return; }
      const selectedColors = new Set();
      this.families.forEach(family => {
        family.colors.forEach(color => {
          color.proxy.checked = color.inputs.every(input => input.checked);
          color.proxy.disabled = color.inputs.every(input => input.disabled);
          if (color.proxy.checked) selectedColors.add(this.source.normalize(color.proxy.dataset.colorLabel));
        });
        const selected = family.colors.filter(color => color.proxy.checked).length;
        family.all.checked = selected > 0 && selected === family.colors.length;
        family.all.indeterminate = selected > 0 && selected < family.colors.length;
        family.button.setAttribute('aria-pressed', String(selected > 0));
        const name = family.button.querySelector('span:last-child');
        name.textContent = selected ? `${family.button.dataset.familyLabel} (${selected})` : family.button.dataset.familyLabel;
      });
      const filter = this.closest('.filter--color-option');
      if (filter) {
        filter.dataset.hasSelectedColors = String(selectedColors.size > 0);
        filter.querySelector('.facets__selected').textContent = selectedColors.size ? `(${selectedColors.size})` : '';
      }
    }

    onClick(event) {
      const button = event.target.closest('[data-drawer-family]');
      if (!button) return;
      const family = this.families[Number(button.dataset.drawerFamily)];
      const open = family.panel.hidden;
      if (family.isAll && open) {
        this.form.querySelectorAll('input[name]').forEach(input => {
          if (input.name === this.source.paramName && input.type === 'checkbox' && !input.disabled) input.checked = false;
        });
        this.refresh();
        button.dispatchEvent(new Event('input', { bubbles: true }));
      }
      this.families.forEach(item => {
        item.panel.hidden = item !== family || !open;
        item.button.setAttribute('aria-expanded', String(item === family && open));
      });
    }

    onChange(event) {
      const family = this.families.find(item => item.panel.contains(event.target));
      if (!family) return;
      const colors = event.target.hasAttribute('data-drawer-select-all') ? family.colors : family.colors.filter(color => color.proxy === event.target);
      colors.forEach(color => color.inputs.forEach(input => {
        if (!input.disabled) input.checked = event.target.checked;
      }));
      this.refresh();
      event.target.dispatchEvent(new Event('input', { bubbles: true }));
    }

    onKey(event) {
      if (event.key !== 'Escape') return;
      const family = this.families.find(item => !item.panel.hidden);
      if (!family) return;
      event.stopPropagation();
      family.panel.hidden = true;
      family.button.setAttribute('aria-expanded', 'false');
      family.button.focus();
    }
  });
}
